import {Inject, Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, Router, UrlTree, Route, Routes} from "@angular/router";
import {BehaviorSubject} from "rxjs";
import {DOCUMENT, Location} from '@angular/common';
import {CacheService} from "../cache/cache.service";
import {ENVIRONMENT, IS_BROWSER} from "../utils/injectiontokens";
import {CryptoService} from "../crypto/crypto.service";
import {CurrencyService} from "../currency/currency.service";
import {ExchangeService} from "../exchange/exchange.service";
import {LocalStorageService} from "../storage/local-storage.service";

declare var gtag: any;

export interface PostMessage {
  type: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  protected routeSnapshot:ActivatedRouteSnapshot;

  protected gclid: string = null;
  protected referral: string = null;

  public queryParams$: BehaviorSubject<string> = new BehaviorSubject(null);
  public currentUrl$: BehaviorSubject<string> = new BehaviorSubject(null);
  public postMessages$: BehaviorSubject<PostMessage> = new BehaviorSubject(null);

  public currentPathTemplate = null;
  public currentPath = null;
  public afw = true;
  public currentQueryParams = null;
  public urlToRedirectAfterLoggedIn: string = null;
  public currentUrlTree: UrlTree;
  public sessionId: string = null;
  protected gaTrackingId;
  public protectedUrls = [
    '/customers/',
    '/dashboard/'
  ];

  public callbackUrls = {};

  public constructor(@Inject(IS_BROWSER) protected isBrowser: boolean,
                     protected router: Router,
                     @Inject(DOCUMENT) protected document:Document,
                     protected cacheService: CacheService,
                     @Inject(ENVIRONMENT) protected environment: any,
                     protected location: Location,
                     protected storageService: LocalStorageService) {

    if(this.isBrowser) {
      const gaTrackingId = this.storageService.getItem("gaTrackingId");
      if(gaTrackingId != null) this.gaTrackingId = this.document.defaultView['gaTrackingId'];
      
      this.gclid = this.storageService.getItem("gclid");
      this.referral = this.storageService.getItem("referral");
      
      const afw = this.storageService.getObject<boolean>("afw");
      if(afw != null) this.afw = afw;
      
      const callbackUrls = this.storageService.getObject<any>("callbackUrls");
      if(callbackUrls != null) this.callbackUrls = callbackUrls;
      
      // Initialize message listener from parent window
      this.initMessageListener();
    }
  }

  /**
   * Initializes the listener for postMessage events from the parent window
   */
  protected initMessageListener(): void {

    if(this.isBrowser) {
      // Check if we are in an iframe
      const isInIframe = window.self !== window.top;
    
      if (isInIframe && window.parent) {
        
        window.addEventListener('message', (event) => {
          try {
            const message = event.data;
            // Validate message format
            if (message && typeof message === 'object' && message.type) {
              // Emit the message to subscribers
              this.postMessages$.next(message);
            }
          } catch (error) {
            console.error('Error processing message:', error);
          }
        }, false);
      
      }
    }

  }

  /**
   * Returns the BehaviorSubject for postMessages
   * Subscribe to this to get updates when messages are received
   */
  public getPostMessages$() {
    return this.postMessages$;
  }

  /**
   * Returns the last received post message
   */
  public getLastPostMessage(): PostMessage {
    return this.postMessages$.getValue();
  }

  public needAuthentication(url: string) {
    for(let i = 0; i < this.protectedUrls.length; i++) {
      if(url.indexOf(this.protectedUrls[i]) !== -1) return true;
    }
    return false;
  }

  public setCurrentUrl(url: string) {
    let urlParts = url.split('?');
    this.currentQueryParams = {};
    this.currentPath = urlParts[0];
    if(urlParts.length > 1) {
      let queryParts = urlParts[1].split('&');
      for (let i = 0; i < queryParts.length; i++) {
        let keyValue = queryParts[i].split('=');
        this.currentQueryParams[keyValue[0]] = keyValue[1];
      }
      this.queryParams$.next(this.currentQueryParams);
      if(this.currentQueryParams.hasOwnProperty('gclid')) {
        this.gclid = this.currentQueryParams['gclid'];
        this.storageService.setItem('gclid', this.gclid);
      }
      if(this.currentQueryParams.hasOwnProperty('referral')) {
        this.referral = this.currentQueryParams['referral'];
        this.storageService.setItem('referral', this.referral);
      }
      if(this.currentQueryParams.hasOwnProperty('afw')) {
        if(this.currentQueryParams['afw'] == 0) {
          this.afw = false;
        }
        else {
          this.afw = true
        }
        this.storageService.setObject('afw', this.afw);
      }

    }
    this.currentUrl$.next(url);
  }

  public setCallbackUrl(code: string, url: string) {
    this.callbackUrls[code] = url;
    this.saveData();
  }

  public getCallbackUrl(code) {
    return this.callbackUrls[code];
  }

  public setUrlToRedirectAfterLoggedIn(url: string) {
    this.urlToRedirectAfterLoggedIn = url;
  }

  public getUrlToRedirectAfterLoggedIn() {
    return this.urlToRedirectAfterLoggedIn;
  }

  public clearUrlToRedirectAfterLoggedIn() {
    this.urlToRedirectAfterLoggedIn = null;
  }

  public clearCallbackUrl(code) {
    delete this.callbackUrls[code];
    this.saveData();
  }

  public setRouteSnapshot(routeSnapshot) {
    this.routeSnapshot = routeSnapshot;
    this.currentPathTemplate = '/' + routeSnapshot.routeConfig.path;
  }

  public getRouteSnapshot() {
    return this.routeSnapshot;
  }

  public getGclid() {
    return this.gclid;
  }

  public getQueryParam(key) {
    //check if key exists
    if(this.currentQueryParams == null) return null;
    if(!this.currentQueryParams.hasOwnProperty(key)) return null;
    return this.currentQueryParams[key];
  }

  public getReferral() {
    return this.referral;
  }

  public getAfw() {
    return this.afw;
  }

  public getSessionId() {
    if(this.sessionId == null) {
      this.sessionId = this.storageService.getItem('sessionId');
      if(this.sessionId == null) {
        this.sessionId = this.generateSessionId();
        this.storageService.setItem('sessionId', this.sessionId);
      }
    }
    return this.sessionId;
  }

  public generateSessionId(): string {
    //generates unique id
    let uniqueId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    return uniqueId;
  }

  public getAnalyticsClientId(): Promise<string> {
    const promise: Promise<string> = new Promise(resolve => {
      if (this.isBrowser) {
        gtag('get', this.gaTrackingId, 'client_id', (clientId) => {
          resolve(clientId);
        });
      }
      else {
        resolve(null);
      }
    });

    return promise;
  }

  public getPageUrlPath(page: number = null) {

    let routeSnapshot: ActivatedRouteSnapshot = this.routeSnapshot;

    let urlTemplate = '/' + routeSnapshot.routeConfig.path;

    let params = routeSnapshot.params;
    let pageReplaced = false;
    for(let key in params) {
      if(key != 'page' || page == null) {
        urlTemplate = urlTemplate.replace('/:' + key, '/' + params[key]);
      }
      else {
        if(page == 1) {
          urlTemplate = urlTemplate.replace('/:page', '');
        }
        else {
          //if the parameter exists, replace it by the page number
          if(urlTemplate.indexOf('/:page') !== -1) {
            urlTemplate = urlTemplate.replace('/:page', '/' + page.toString());
          }
          //otherwise we'll geassume that page is the last parameter
          else {
            urlTemplate = urlTemplate + '/' + page.toString();
          }
        }
        pageReplaced = true;
      }
    }

    if(!pageReplaced && page != null && page != 1) {
      urlTemplate = urlTemplate + '/' + page.toString();
    }

    return urlTemplate;

  }

  public getQueryParams() {
    return this.currentQueryParams;
  }

  public getCurrentUrl() {
    return this.currentUrl$.getValue();
  }

  public getCurrentPathTemplate() {
    //if (this.currentPathTemplate) {
    //  return this.currentPathTemplate;
    //}
    
    // Get the current URL and try to extract its route template
    const currentUrl = this.getCurrentUrl();
    if (!currentUrl) {
      return null;
    }
    
    // Clean the URL from query parameters
    const urlWithoutParams = currentUrl.split('?')[0];
    
    // Get route information from the router state
    const routerState = this.router.routerState;
    const snapshot = routerState.snapshot;
    const root = snapshot.root;
    
    // Extract route parameters from the activated route
    const params = this.getAllRouteParams(root);
    
    // Replace parameter values in the URL with their parameter names
    let template = urlWithoutParams;
    
    // Sort keys by length (descending) to prevent partial replacements
    const paramKeys = Object.keys(params).sort((a, b) => params[b].length - params[a].length);
    
    for (const key of paramKeys) {
      const value = params[key];
      if (value && typeof value === 'string') {
        template = template.replace(value, `:${key}`);
      }
    }
    
    // Cache the result
    this.currentPathTemplate = template;
    
    return template;
  }
  
  /**
   * Recursively collects all route parameters from an ActivatedRouteSnapshot
   */
  private getAllRouteParams(route: ActivatedRouteSnapshot, params: {[key: string]: string} = {}): {[key: string]: string} {
    // Add route parameters from current route
    if (route.params) {
      for (const key in route.params) {
        if (route.params.hasOwnProperty(key)) {
          params[key] = route.params[key];
        }
      }
    }
    
    // Recursively process child routes
    if (route.children) {
      for (const child of route.children) {
        this.getAllRouteParams(child, params);
      }
    }
    
    return params;
  }

  public getCurrentPath() {
    return this.currentPath;
  }

  public saveData() {
    if(this.isBrowser) {
      this.storageService.setObject('callbackUrls', this.callbackUrls);
    }
  }

}
